import { useAuthStore } from '../auth/authStore';

/** The router's base URL. Absolute, and deliberately not a Vite proxy: a proxy
 *  masks the server's real state and misattributes backend failures to the
 *  frontend. The dev origin lives in the server's CORS allowlist instead. */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

/** ApiError carries the status so a caller can tell "you are not an operator"
 *  from "the warehouse is down" — the console renders those very differently. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** idToken returns the signed-in operator's Firebase ID token.
 *
 *  `getIdToken()` refreshes on its own when the token is near expiry, so the
 *  right thing is to ask for it per request rather than cache one. */
async function idToken(force = false): Promise<string> {
  const user = useAuthStore.getState().user;
  if (!user) throw new ApiError(401, 'not signed in');
  return user.getIdToken(force);
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const call = async (force: boolean) => {
    const res = await fetch(BASE + path, {
      method,
      // Never cache an admin response. One of these routes returns a customer's
      // prompt, and a cached copy could be re-read without the server writing
      // the audit row that justifies reading it at all — and could outlive the
      // retention the estate chose. The rest lose nothing by being fresh.
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${await idToken(force)}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return res;
  };

  let res = await call(false);
  // One forced refresh on a 401. A token can be rejected because Google
  // rotated its signing key between the cached token and this request, and
  // that is indistinguishable from a real rejection until you retry.
  if (res.status === 401) res = await call(true);

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      // A non-JSON error body is still an error; the status carries the meaning.
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => send<T>('GET', path),
  post: <T>(path: string, body?: unknown) => send<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => send<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => send<T>('PUT', path, body),
  del: <T>(path: string) => send<T>('DELETE', path),
};

/** query builds a query string, dropping empty values so an absent scope does
 *  not become `estateId=` — which the server would read as a real filter. */
export function query(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}
